// C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\MerchantDashboard.tsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantLayout from "../components/MerchantLayout";
import "../../App.css";
export {}; // keep module safety

type MerchantMe = {
  merchant_id: string;
  company_name: string;
  status: string;
  approval_status?: string; // ← ADD THIS


  merchant_type?: string; // "個人" | "法人" | ""

  docs_total: number;
  docs_completed: number;
  docs_percent: number;

  uploads_total: number;
  uploads_completed: number;
  uploads_percent: number;

  created_at: string;
  updated_at: string;
};

function DonutProgress({
  title,
  completed,
  total,
  percent,
  note,
}: {
  title: string;
  completed: number;
  total: number;
  percent: number;
  note: string;
}) {
  const p = Math.min(100, Math.max(0, Number.isFinite(percent) ? percent : 0));
  const done = p >= 100;

  // CSS conic-gradient donut
  const ringStyle: React.CSSProperties = {
    width: 120,
    height: 120,
    borderRadius: "50%",
    background: `conic-gradient(${done ? "#16a34a" : "#0b1f3b"} ${p}%, #e2e8f0 ${p}% 100%)`,
    display: "grid",
    placeItems: "center",
    position: "relative",
  };

  const holeStyle: React.CSSProperties = {
    width: 86,
    height: 86,
    borderRadius: "50%",
    background: "#fff",
    display: "grid",
    placeItems: "center",
    border: "1px solid #e2e8f0",
  };

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 14,
        background: "#fff",
        display: "flex",
        gap: 14,
        alignItems: "center",
      }}
    >
      <div style={ringStyle} aria-label={`${title} ${p}%`}>
        <div style={holeStyle}>
          <div style={{ textAlign: "center", lineHeight: 1.1 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: done ? "#16a34a" : "#0b1f3b" }}>
              {p}%
            </div>
            <div style={{ fontSize: 11, fontWeight: 900, color: "#334155" }}>
              {completed}/{total}
            </div>
          </div>
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 900, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>{note}</div>

        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #e2e8f0",
              background: done ? "#ecfdf5" : "#f8fafc",
              color: done ? "#166534" : "#334155",
              fontWeight: 900,
              fontSize: 12,
            }}
          >
            {done ? "完了" : "進行中"}
          </span>

          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: "#0f172a",
              fontWeight: 900,
              fontSize: 12,
            }}
          >
            残り：{Math.max(0, total - completed)}
          </span>
        </div>
      </div>
    </div>
  );
}



function statusColor(status: string): string {
  const s = (status || "").trim();

  // ✅ Green
  if (s === "承認済") return "#16a34a";

  // ✅ Red
  if (s === "提出済（承認待ち）") return "#dc2626";

  // ✅ Grey (未着手 / 進行中 / default)
  return "#64748b";
}


const MerchantDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<MerchantMe | null>(null);
  const [error, setError] = useState("");

  const [merchantType, setMerchantType] = useState<"個人" | "法人" | "">("");
const [savingType, setSavingType] = useState(false);
const [typeError, setTypeError] = useState("");
const [showTypeSelection, setShowTypeSelection] = useState(false);

  const logout = () => {
    localStorage.removeItem("merchant_access_token");
    localStorage.removeItem("merchant_id");
    localStorage.removeItem("merchant_company_name");
    navigate("/merchant-login");
  };

  const saveMerchantType = async (nextType: "個人" | "法人") => {
        const confirmed = window.confirm(`${nextType}を選択して入力画面へ進みますか？`);
        if (!confirmed) return;
    setTypeError("");
    const token = localStorage.getItem("merchant_access_token");
    if (!token) {
      navigate("/merchant-login");
      return;
    }

    try {
      setSavingType(true);

      const res = await fetch("/api/merchant/set-merchant-type", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ merchant_type: nextType }),
      });

      if (res.status === 401) {
        logout();
        return;
      }

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setTypeError(json?.detail || "区分の保存に失敗しました。");
        return;
      }

      setMerchantType(nextType);
      setMe((prev) => (prev ? { ...prev, merchant_type: nextType } : prev));

      navigate("/merchant/forms/company-overview");
    } catch {
      setTypeError("区分の保存に失敗しました。バックエンドが起動しているか確認してください。");
    } finally {
      setSavingType(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      setError("");
      const token = localStorage.getItem("merchant_access_token");

      if (!token) {
        navigate("/merchant-login");
        return;
      }

      try {
        const res = await fetch("/api/merchant/me", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401) {
          logout();
          return;
        }

        if (!res.ok) {
          setError("加盟店情報の取得に失敗しました。");
          setLoading(false);
          return;
        }

        const json = await res.json();
        setMe(json.merchant);
        setMerchantType((json.merchant?.merchant_type || "") as any);
        setLoading(false);
      } catch {
        setError("加盟店情報の取得に失敗しました。バックエンドが起動しているか確認してください。");
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const typeSelected =
  merchantType === "個人" || merchantType === "法人";

const newApplicationDisabled = typeSelected;
const continueApplicationDisabled = !typeSelected;

  const continueApplication = () => {
  navigate("/merchant/register");
};

  return (
    <MerchantLayout title="メインメニュー" active="dashboard">
      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && me && (
        <>
          {/* ✅ Account info */}
          <div
            style={{
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: 12,
              padding: 14,
              marginBottom: 16,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>アカウント情報</div>
            <div style={{ lineHeight: 1.8 }}>
              <div>加盟店ID：{me.merchant_id}</div>
              <div>会社名：{me.company_name}</div>
              {(() => {
  const displayStatus =
    (me.approval_status || "").trim() === "承認済" ? "承認済" : (me.status || "").trim();

  return (
    <div style={{ fontWeight: 900 }}>
  <span style={{ color: "#0f172a" }}>ステータス：</span>
  <span style={{ color: statusColor(displayStatus), marginLeft: 4 }}>
    {displayStatus}
  </span>
</div>

  );
})()}


            </div>
          </div>

          {/* ✅ 申請開始メニュー */}
<div
  style={{
    background: "#ecfeff",
    border: "1px solid #a5f3fc",
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
  }}
>
  <div style={{ fontWeight: 900, marginBottom: 14 }}>加盟店契約申請</div>

  <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
    <button
  type="button"
  disabled={newApplicationDisabled}
  onClick={() => {
    if (newApplicationDisabled) return;
    setShowTypeSelection(true);
  }}
  style={{
        minWidth: 180,
        padding: "12px 20px",
        borderRadius: 4,
        border: "1px solid #0f172a",
        background: newApplicationDisabled ? "#e5e7eb" : "#fff7d6",
color: newApplicationDisabled ? "#94a3b8" : "#0f172a",
fontWeight: 900,
cursor: newApplicationDisabled ? "not-allowed" : "pointer",
      }}
    >
      新規申請
    </button>

    <button
  type="button"
  disabled={continueApplicationDisabled}
  onClick={() => {
    if (continueApplicationDisabled) return;
    continueApplication();
  }}
  style={{
        minWidth: 180,
        padding: "12px 20px",
        borderRadius: 4,
        border: "1px solid #0f172a",
        background: continueApplicationDisabled ? "#e5e7eb" : "#fff7d6",
color: continueApplicationDisabled ? "#94a3b8" : "#0f172a",
fontWeight: 900,
cursor: continueApplicationDisabled ? "not-allowed" : "pointer",
      }}
    >
      途中から始める
    </button>
  </div>
</div>

{showTypeSelection && !typeSelected && (
  <div
    style={{
      background: typeSelected ? "#ecfeff" : "#fff7ed",
      border: `1px solid ${typeSelected ? "#a5f3fc" : "#fed7aa"}`,
      borderRadius: 12,
      padding: 14,
      marginBottom: 16,
    }}
  >
    <div style={{ fontWeight: 900, marginBottom: 10 }}>区分（個人／法人）</div>

    {!typeSelected && me.status !== "完了" && (
      <div
        style={{
          marginBottom: 10,
          background: "#fffbeb",
          border: "1px solid #f59e0b",
          color: "#92400e",
          padding: "10px 12px",
          borderRadius: 12,
          fontWeight: 900,
          fontSize: 13,
        }}
      >
        ⚠ 区分（個人／法人）を選択してください。区分を選択するまで、書類アップロードはできません。
      </div>
    )}

    {typeError && (
      <div
        style={{
          marginBottom: 10,
          background: "#fff1f2",
          border: "1px solid #fecdd3",
          color: "#b91c1c",
          padding: "10px 12px",
          borderRadius: 12,
          fontWeight: 800,
          fontSize: 13,
        }}
      >
        {typeError}
      </div>
    )}

    {me.status === "完了" ? (
      <div style={{ fontWeight: 900, color: "#475569" }}>
        {typeSelected ? `選択済み：${merchantType}` : "未選択"}
      </div>
    ) : (
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={savingType}
          onClick={() => saveMerchantType("個人")}
          style={{
            padding: "10px 16px",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            background: "#ffffff",
color: "#0f172a",
            fontWeight: 900,
            cursor: savingType ? "not-allowed" : "pointer",
            boxShadow: "none",
          }}
        >
          個人
        </button>

        <button
          type="button"
          disabled={savingType}
          onClick={() => saveMerchantType("法人")}
          style={{
            padding: "10px 16px",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            background: "#ffffff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: savingType ? "not-allowed" : "pointer",
            boxShadow: "none",
          }}
        >
          法人
        </button>

        <span style={{ fontSize: 12, fontWeight: 900, color: "#475569" }}>
          {savingType ? "保存中..." : typeSelected ? `選択済み：${merchantType}` : "未選択"}
        </span>
      </div>
    )}
  </div>
)}

          {/* ✅ Progress */}
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 10 }}>進捗状況</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <DonutProgress
                title="フォーム入力（7件）"
                completed={me.docs_completed}
                total={me.docs_total}
                percent={me.docs_percent}
                note="必要情報の入力"
              />

              <DonutProgress
                title={`書類アップロード（${me.uploads_total}件）`}
                completed={me.uploads_completed}
                total={me.uploads_total}
                percent={me.uploads_percent}
                note="指定PDFのアップロード"
              />
            </div>

            <div style={{ marginTop: 14, color: "#475569", fontSize: 13 }}>
              最終更新：{me.updated_at}
            </div>
          </div>
        </>
      )}
    </MerchantLayout>
  );
};

export default MerchantDashboard;
